/* eslint-disable no-alert */
import React from 'react';
import * as RNW from 'react-native-web';

export * from 'react-native-web';

const isCancelButton = (button) => {
  if (!button) return false;
  if (button.style === 'cancel') return true;
  if (typeof button.text === 'string') {
    const text = button.text.trim().toLowerCase();
    return /^(cancel|hủy|huỷ|h[uủụ][yỷ]|bỏ qua|bo qua|không|khong|đóng|dong|close|no|quay lại|quay lai|取消|ຍົກເລີກ)$/i.test(text);
  }
  return false;
};

export const Alert = {
  alert: (title = '', message = '', buttons = [], options = {}) => {
    if (typeof window === 'undefined') return;
    const text = [title, message].filter(Boolean).join('\n') || 'Notice';

    if (!Array.isArray(buttons) || buttons.length === 0) {
      if (typeof window.alert === 'function') {
        window.alert(text);
      }
      return;
    }

    if (buttons.length === 1) {
      if (typeof window.alert === 'function') {
        window.alert(text);
      }
      buttons[0]?.onPress?.();
      return;
    }

    if (typeof window.confirm === 'function') {
      const confirmed = window.confirm(text);
      let cancelButton = buttons.find((btn) => isCancelButton(btn));
      let confirmButton = buttons.find((btn) => btn !== cancelButton && !isCancelButton(btn));

      if (!cancelButton && !confirmButton) {
        const withPress = buttons.find((btn) => typeof btn?.onPress === 'function');
        const withoutPress = buttons.find((btn) => typeof btn?.onPress !== 'function');
        if (withPress && withoutPress) {
          confirmButton = withPress;
          cancelButton = withoutPress;
        } else {
          confirmButton = buttons[0];
          cancelButton = buttons[1];
        }
      } else if (!cancelButton) {
        cancelButton = buttons.find((btn) => btn !== confirmButton) || buttons[1];
      } else if (!confirmButton) {
        confirmButton = buttons.find((btn) => btn !== cancelButton) || buttons[0];
      }

      if (confirmed) {
        confirmButton?.onPress?.();
      } else {
        cancelButton?.onPress?.();
        options?.onDismiss?.();
      }
    } else {
      if (typeof window.alert === 'function') {
        window.alert(text);
      }
      const confirmButton = buttons.find((btn) => !isCancelButton(btn)) || buttons[0];
      confirmButton?.onPress?.();
    }
  }
};

export const sanitizeStyle = (style) => {
  if (!style) return style;
  if (Array.isArray(style)) {
    return style.map(sanitizeStyle);
  }
  if (typeof style === 'object') {
    const keys = Object.keys(style);
    let hasNumericKey = false;
    for (let i = 0; i < keys.length; i += 1) {
      if (/^\d+$/.test(keys[i])) {
        hasNumericKey = true;
        break;
      }
    }
    if (!hasNumericKey) return style;

    const { cleaned, arrayItems } = keys.reduce(
      (acc, key) => {
        if (/^\d+$/.test(key)) {
          acc.arrayItems.push(style[key]);
        } else {
          acc.cleaned[key] = style[key];
        }
        return acc;
      },
      { cleaned: {}, arrayItems: [] }
    );
    return [cleaned, ...arrayItems.map(sanitizeStyle)];
  }
  return style;
};

const OriginalView = RNW.View;
export const View = React.forwardRef((props, ref) => {
  if (props?.style) {
    return <OriginalView {...props} ref={ref} style={sanitizeStyle(props.style)} />;
  }
  return <OriginalView {...props} ref={ref} />;
});
View.displayName = 'View';

const OriginalText = RNW.Text;
export const Text = React.forwardRef((props, ref) => {
  if (props?.style) {
    return <OriginalText {...props} ref={ref} style={sanitizeStyle(props.style)} />;
  }
  return <OriginalText {...props} ref={ref} />;
});
Text.displayName = 'Text';

const OriginalScrollView = RNW.ScrollView;
export const ScrollView = React.forwardRef((props, ref) => {
  if (props?.style || props?.contentContainerStyle) {
    return (
      <OriginalScrollView
        {...props}
        ref={ref}
        style={props.style ? sanitizeStyle(props.style) : undefined}
        contentContainerStyle={props.contentContainerStyle ? sanitizeStyle(props.contentContainerStyle) : undefined}
      />
    );
  }
  return <OriginalScrollView {...props} ref={ref} />;
});
ScrollView.displayName = 'ScrollView';

const OriginalPressable = RNW.Pressable;
export const Pressable = React.forwardRef((props, ref) => {
  if (typeof props?.style === 'function') {
    return <OriginalPressable {...props} ref={ref} style={(state) => sanitizeStyle(props.style(state))} />;
  }
  if (props?.style) {
    return <OriginalPressable {...props} ref={ref} style={sanitizeStyle(props.style)} />;
  }
  return <OriginalPressable {...props} ref={ref} />;
});
Pressable.displayName = 'Pressable';

const originalFlatten = RNW.StyleSheet?.flatten;
export const StyleSheet = {
  ...RNW.StyleSheet,
  flatten: (style) => (originalFlatten ? originalFlatten(sanitizeStyle(style)) : sanitizeStyle(style))
};

const shim = {
  ...RNW,
  Alert,
  View,
  Text,
  ScrollView,
  Pressable,
  StyleSheet,
  sanitizeStyle
};

export default shim;
